import React, { useEffect, useState } from "react";
import { Coins, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * GreenCoins balance + milestone tooltip that works on hover AND tap.
 * Uses Popover so it opens on click (mobile) and on hover (desktop via CSS).
 */
const GreenCoinsBalance = ({ balance, testId = "greencoins-balance" }) => {
  const [milestone, setMilestone] = useState(1000);
  useEffect(() => {
    api.get("/config/milestones").then((r) => setMilestone(r.data.scratch_card_milestone || 1000)).catch(() => {});
  }, []);
  const remaining = Math.max(0, milestone - balance);
  const unlocked = balance >= milestone;
  const pct = Math.min(100, (balance / milestone) * 100);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-2 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-4 py-2 font-semibold cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
          data-testid={testId}
          aria-label="View GreenCoin milestone info"
        >
          <Coins className="h-4 w-4" />
          <span className="mono">{balance.toLocaleString()}</span>
          <span className="text-xs opacity-80">GreenCoins</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" data-testid="greencoins-popover">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <div className="font-bold text-sm">Scratch Card Milestone</div>
        </div>
        {unlocked ? (
          <p className="text-sm text-emerald-700 dark:text-emerald-300 font-semibold">🎉 You've crossed {milestone.toLocaleString()} GreenCoins and unlocked a Scratch Card!</p>
        ) : (
          <>
            <p className="text-sm text-foreground/85">Keep going! You're <strong>{remaining.toLocaleString()}</strong> GreenCoins away from your Scratch Card.</p>
            <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} data-testid="milestone-progress" />
            </div>
            <div className="mt-1.5 flex justify-between text-[10px] mono text-muted-foreground">
              <span>{balance.toLocaleString()}</span>
              <span>{milestone.toLocaleString()}</span>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default GreenCoinsBalance;
